/**
 * SecondChannel — duplex v2's side-voice (docs/duplex-v2.md): backchannels, fillers and bridges in <300ms while the main
 * agent's history stays clean. Lines go to the host's `speak`/`show` only — never to an Agent.
 *
 * Hot path: a rule gate decides at 0ms and a canned bank speaks at 0ms. The decider only REFINES a candidate the rule
 * already proposed, inside `decisionBudgetMs`; a late answer is dropped. Refiner: fastChannelDecider (~100ms);
 * the generic Choice (Instinct.channel) missed the budget on every call (p50 ~930ms on groq) — see channelDecide.
 *
 * Channel-1 (the main answer) lifecycle via onAgentState: a bridge only masks a SLOW start — it waits `bridgeDelayMs` and
 * plays only while channel-1 has produced nothing yet (its first token or audio drops it); once channel-1 responds no new
 * line starts. STACK, DON'T CUT: an in-flight line drains to its end — hosts queue channel-1 audio
 * behind `drain()` (gapless); only the user's barge-in (onBargeIn) cuts a line.
 */
import { forComponent } from '../logging';
import { CHANNEL_ACTIONS, type ChannelKind, type Instinct } from './instinct';
import type { ChatLike, ChatResponse } from '../llm';
import { matchCue, silentTurn, type BcPlan, type Cue, type PlanContext } from './backchannelPlan';
import { cleanLine, detectLanguage, EN_BANK, isPhatic, languageName, norm, pendingQuestion, personaHint, primaryLang, requestOpener, type ChannelBanks, type Gender } from './channelLang';

const log = forComponent('SecondChannel');

export type LineKind = Exclude<ChannelKind, 'silence'>;
/** The main (channel-1) response lifecycle. `idle` = no turn in flight (the user has the floor). */
export type MainState = 'idle' | 'queued' | 'thinking' | 'streaming_text' | 'first_audio' | 'speaking';
type Trigger = 'interim' | 'pause' | 'working';

/** The micro-context a generator gets: a tiny tail, never history. */
export interface ChannelContext {
  kind: LineKind;
  /** The last ~microWords words the user said. */
  heard: string;
  state: MainState;
  /** The last few channel lines — do not reuse. */
  avoid: string[];
  /** The user's language (BCP-47 primary code) — the line is written in it. */
  lang: string;
  /** A content reaction (chatty): "oh no", "wait, really?", a key-word echo — not just an "mm-hm". */
  react?: boolean;
}

export interface ChannelEvent {
  trigger: Trigger;
  rule: ChannelKind;
  choice?: string;
  acted: 'spoken' | 'silence' | 'stale' | 'gated' | 'skipped' | 'no_bank';
  kind?: LineKind;
  text?: string;
  /** The backchannel came from a planned cue (anticipate) — its matched line. */
  cue?: string;
  ms: number;
}

/** How talkative the second channel is (`--channel-mode`). */
export type ChannelMode = 'turn' | 'normal' | 'chatty';

/** Mode presets over SecondChannelOptions; explicit options win over the preset.
 *  turn:   no backchannels, no fillers — only a bridge when channel-1 is slow.
 *  normal: the defaults.
 *  chatty: an engaged listener — planned cues at short breaths, earlier/denser light cues, and content reactions
 *          ("oh no", "wait, really?", "the deploy?") written by the fast model, spaced apart (light "mm" in between).
 *          Needs echo cancellation: with aec off it runs on normal timing (our lines would re-enter the mic). */
export const CHANNEL_MODES: Record<ChannelMode, Partial<SecondChannelOptions>> = {
  turn: { kinds: ['bridge'], bridgeDelayMs: 1500 },
  normal: {},
  chatty: {
    anticipate: true, cueOnly: false, generateFor: ['backchannel', 'bridge'], reactionGapMs: 6000, generateBudgetMs: 400,
    // A breath must outlast the word cadence: at ~2.5 words/s interims arrive every ~400ms, so a 250ms "breath" fired after
    // every word and only the 1.2s gaps held it (~25 lines per 30s, live probe). Lines ≥2.5s apart (light after light 3s), ≤6 in any 30s.
    backchannelMinWords: 5, backchannelGapMs: 3000, minGapMs: 2500, backchannelPauseMs: 500, maxPerWindow: 6,
  },
};
export const CHANNEL_MODE_NAMES = Object.keys(CHANNEL_MODES) as ChannelMode[];
/** A `--channel-mode` / AGENTX_CHANNEL_MODE value → a mode (throws on an unknown one; empty → normal). */
export function parseChannelMode(v?: string): ChannelMode {
  if (!v) return 'normal';
  if (!(v in CHANNEL_MODES)) throw new Error(`unknown channel mode "${v}" (${CHANNEL_MODE_NAMES.join('|')})`);
  return v as ChannelMode;
}

export class SecondChannelOptions {
  /** Preset (CHANNEL_MODES) under the explicit options. */
  mode: ChannelMode = 'normal';
  /** Line kinds this channel may speak. */
  kinds: LineKind[] = ['backchannel', 'filler', 'bridge'];
  /** A generated (content) backchannel needs this long since the last one; in between, canned light cues. 0 = always. */
  reactionGapMs = 0;
  /** Refiner (Jev/fast LLM): the choice over CHANNEL_ACTIONS, or null on failure. Absent → the rule alone decides.
   *  `state.rule` is the kind the rule proposed (fastChannelDecider answers yes/no on it). `signal` aborts a late call. */
  decide?: (heard: string, state: Record<string, unknown>, signal?: AbortSignal) => Promise<{ choice: string } | null>;
  /** Fast-model line writer (see fastLineGenerator). Absent or late → the canned bank. */
  generate?: (ctx: ChannelContext, signal: AbortSignal) => Promise<string>;
  /** Per-language banks (ChannelBanks). Absent → the hardcoded English bank, and silence in any other language. */
  banks?: Pick<ChannelBanks, 'get' | 'ensure'>;
  /** The host's language (BCP-47; pinned — detection never overrides it). Absent → unknown until the user's words
   *  tell it confidently; the channel is silent until then (never English by default). */
  lang?: string;
  /** Kinds worth generating (content-conditioned); the rest are canned sounds. */
  generateFor: LineKind[] = ['bridge']; // a backchannel is timing, not content: canned at 0ms (a generated one arrived after the micro-pause)
  /** Host TTS on the second channel; resolves when the line finished playing. `false` = the host could not play it. */
  speak: (text: string, kind: LineKind) => Promise<unknown> | boolean | void = () => {};
  /** Host UI (caption / muted line) — never history. */
  show: (text: string, kind: LineKind) => void = () => {};
  /** Stop the in-flight line's audio now (barge-in only). */
  cancel: () => void = () => {};
  onEvent: (e: ChannelEvent) => void = () => {};
  /** Echo cancellation on: backchannels may play while the user speaks. Off (half-duplex) → fillers/bridges in silences only. */
  aec = false;
  decideEveryWords = 3;
  decisionBudgetMs = 300;
  generateBudgetMs = 500;
  minGapMs = 2500;
  /** Backchannel after backchannel needs this gap (~4-6 cues in a 30s monologue — Para phone call 2026-10-13: 5s left
   *  one cue in a long statement). */
  backchannelGapMs = 3000;
  /** Soft cap: at most this many backchannels in any `windowMs` (0 = none). */
  maxPerWindow = 0;
  windowMs = 30_000;
  /** The user resumes within this long of their last words after a line of theirs was dispatched: the same statement goes
   *  on (an STT endpoint mid-monologue) — its words still count toward the backchannel guards, and it is no fresh request. */
  continueMs = 3000;
  /** A decision whose partial grew by this many words while in flight is stale → dropped. */
  staleWords = 3;
  microWords = 30;
  avoidLines = 4;
  /** Canned picks exclude the last N picks of the same kind. */
  bankExclude = 2;
  /** A backchannel needs a substantial chunk first: a turn of ≤8 words is a request/answer, not a story (replay: most
   *  misplaced "mm-hm"s fired at words 6-8 of a short command). */
  backchannelMinWords = 10;
  /** A turn that opens as a question/command (requestOpener) holds backchannels until it has grown into an explanation. */
  requestHoldWords = 18;
  /** No new interim for this long mid-utterance (a breath / clause gap, shorter than a pause) → a backchannel moment. */
  backchannelPauseMs = 350;
  /** A bridge waits this long after the user's turn is dispatched, and plays only if channel-1 has said nothing yet
   *  (a fast reply/ack needs no bridge — one before it reads as the agent starting a different sentence). 0 = at once. */
  bridgeDelayMs = 1200;
  /** Duplex v3 (§3 first sound): a bridge skips the decider (the veto stays for backchannels/fillers) and speaks a
   *  canned line at once; a generated one is started at dispatch and used only if it already landed. */
  instantBridge = false;
  /** No new interim for this long → a pause (hosts may also call onPause). */
  fillerPauseMs = 700;
  fillerMinWords = 4;
  bridgeMinWords = 5;
  /** ANTICIPATE (backchannelPlan.ts): plan the user's next turn off the hot path (after the agent's reply / at turn start,
   *  revised once at `reviseAtWords`); live, a backchannel fires only on a planned cue, at a `cuePauseMs` breath, with the
   *  cue's line and no decider call. A silent plan (short command / question / density none) vetoes backchannels. No plan
   *  yet / failed / late → the reactive path. Off by default (AGENTX_CHANNEL_ANTICIPATE=1). */
  anticipate = false;
  plan?: (ctx: PlanContext, signal: AbortSignal) => Promise<BcPlan | null>;
  planBudgetMs = 4000;
  reviseAtWords = 7;
  /** A predicted moment needs only a short breath (the decision is already made). */
  cuePauseMs = 200;
  /** A cue counts only within the last N words (older matches were gated or passed). */
  cueWindow = 6;
  /** true: a plan fires on its cues only. false: a non-silent plan with density normal/high also lets the reactive breath
   *  backchannel through (the plan then only vetoes short commands / questions / low density). */
  cueOnly = true;
  onPlan: (p: BcPlan, revised: boolean, ms: number) => void = () => {};
  now: () => number = () => performance.now();
  random: () => number = Math.random;
}

const words = (s: string) => (s.trim() ? s.trim().split(/\s+/) : []);

/** Wait for `p` at most `ms`; null when late (the caller's fallback runs; the late call is aborted via `ac`). */
function withBudget<T>(p: Promise<T>, ms: number, what: string, ac?: AbortController): Promise<T | null> {
  let t: ReturnType<typeof setTimeout>;
  const late = new Promise<null>((r) => { t = setTimeout(() => { log.debug(`${what}: over the ${ms}ms budget — dropped`); ac?.abort(); r(null); }, ms); });
  return Promise.race([p.catch((e) => {
    const msg = String((e as Error)?.message ?? e);
    if ((e as Error)?.name === 'AbortError' || /aborted/i.test(msg)) log.debug(`${what}: aborted after its budget`); // our own abort of a late call — not a failure
    else log.warn(`${what} failed — falling back: ${msg}`);
    return null;
  }), late]).finally(() => clearTimeout(t));
}

export class SecondChannel {
  private heard = '';
  private userSpeaking = false;
  private main: MainState = 'idle';
  private gen = 0; // bumped by barge-in / a new user turn → in-flight decisions drop
  private decidedAt = 0; // word count at the last decision
  private busy = false;
  private pauseTimer: ReturnType<typeof setTimeout> | null = null;
  private bcTimer: ReturnType<typeof setTimeout> | null = null;
  private bridgeTimer: ReturnType<typeof setTimeout> | null = null;
  private lastAt = -Infinity;
  private lastKind: LineKind | null = null;
  private live: string | null = null; // our last line, until channel-1 audio takes the floor
  private cutLine: string | null = null; // the line the last barge actually cut (spoke())
  private bridged = false; // one bridge per main turn
  private bridgeDraft: { gen: number; text: string } | null = null; // a bridge generated ahead, used only if ready in time
  private recent: string[] = [];
  private picks = new Map<LineKind, string[]>();
  private inflight: Promise<void> | null = null;
  private readonly startedAt: number;
  private lang: string | null = null;
  private carried = 0; // words of this statement already dispatched (a monologue split by STT endpoints)
  private floorWords = 0; // carried + the last dispatched line's words
  private lastHeardAt = -Infinity;
  private pinned = false;
  // anticipate: the plan for the user's current/next turn (turnNo bumps per dispatched turn → a late plan is dropped)
  private turnNo = 0;
  private plan: BcPlan | null = null;
  private planning = false;
  private planTries = 0; // per turn: the reply-end plan + one turn-start retry, never a call per interim
  private revised = false;
  private cueFrom = 0; // word index after the last planned backchannel
  private reply = ''; // channel-1's text this turn (the plan's context)
  private lastUser = '';
  private lastReactAt = -Infinity;
  private normalTiming: Partial<SecondChannelOptions> = {}; // chatty without aec: the preset's keys back at their defaults
  private degradedLogged = false;

  public constructor(public options?: Partial<SecondChannelOptions>) {
    const preset = CHANNEL_MODES[options?.mode ?? 'normal'] ?? {};
    const base = new SecondChannelOptions();
    this.options = { ...base, ...preset, ...options };
    for (const k of Object.keys(preset) as (keyof SecondChannelOptions)[]) if (!options || !(k in options)) (this.normalTiming as any)[k] = base[k];
    this.startedAt = this.o.now();
    if (this.o.lang) this.setLanguage(this.o.lang, true);
  }

  /** The language lines are spoken in (detected from the user's words, or set by the host). */
  get language(): string | null { return this.lang; }

  /** Switch language: its bank starts loading in the background. `host` = the host's STT-reported language — it pins
   *  the language (later detection never overrides it; only another host call does). Tags normalize to 'he-IL' → 'he'. */
  setLanguage(tag: string, host = false): void {
    if (!tag || (this.pinned && !host)) return;
    if (host) this.pinned = true;
    const lang = primaryLang(tag);
    if (!lang || lang === this.lang) return;
    log.debug(`language ${this.lang} → ${lang}`);
    this.lang = lang;
    this.recent = [];
    this.picks.clear();
    void this.o.banks?.ensure(lang);
  }
  /** Once a language is known, switching needs strong evidence (detectLanguage's `switching`): an English "Um," read as
   *  Portuguese flipped a live session to pt (silence until a pt bank was generated — it then spoke Portuguese). */
  private detect(text: string): void { if (this.pinned) return; const l = detectLanguage(text, !!this.lang); if (l) this.setLanguage(l); }
  private get bank() { return this.lang ? (this.o.banks ? this.o.banks.get(this.lang) : this.lang === 'en' ? EN_BANK : null) : null; }
  private get o(): SecondChannelOptions {
    const o = this.options as SecondChannelOptions;
    return o.mode !== 'chatty' || o.aec ? o : { ...o, ...this.normalTiming };
  }

  /** The user's live partial for the current turn (the whole partial, not a delta). */
  onInterim(text: string): void {
    const t = text.trim();
    if (!t || t === this.heard) return;
    if (this.main !== 'idle' || !this.heard) this.carried = this.o.now() - this.lastHeardAt < this.o.continueMs ? this.floorWords : 0; // a new line: the same statement going on, or a fresh turn
    this.lastHeardAt = this.o.now();
    if (this.main !== 'idle') this.onAgentState('idle'); // the user speaks again: a new turn (also after a tool-only turn that never reached audio)
    this.heard = t;
    this.detect(t);
    this.userSpeaking = true;
    if (this.pauseTimer) clearTimeout(this.pauseTimer);
    this.pauseTimer = setTimeout(() => { this.pauseTimer = null; this.onPause(this.o.fillerPauseMs); }, this.o.fillerPauseMs);
    // A breath between clauses (interims stop briefly, the user keeps the floor): the natural backchannel moment. STT
    // partials rarely end on punctuation before the endpoint finalizes the line, so punctuation alone almost never fired.
    let cue: Cue | null = null;
    if (this.o.anticipate) {
      const n = words(t).length;
      if (!this.plan && !this.planning && this.planTries < 2) this.requestPlan(); // turn start with no plan (first turn / a failed one)
      else if (this.plan && !this.revised && n >= this.o.reviseAtWords) { this.revised = true; this.requestPlan(t); }
      cue = matchCue(this.plan, t, Math.max(this.cueFrom, n - this.o.cueWindow));
    }
    if (this.bcTimer) clearTimeout(this.bcTimer);
    this.bcTimer = this.o.aec ? setTimeout(() => { this.bcTimer = null; if (this.userSpeaking && this.heard === t) void this.consider('interim', 0, true, cue); }, cue ? this.o.cuePauseMs : this.o.backchannelPauseMs) : null;
    if (words(t).length - this.decidedAt >= this.o.decideEveryWords) void this.consider('interim');
  }

  /** The user went quiet for `ms` (host VAD/STT pause, or the internal interim timer). */
  onPause(ms: number): void {
    if (this.pauseTimer) { clearTimeout(this.pauseTimer); this.pauseTimer = null; }
    if (this.bcTimer) { clearTimeout(this.bcTimer); this.bcTimer = null; }
    this.userSpeaking = false;
    void this.consider('pause', ms);
  }

  /** Channel-1 lifecycle. `queued` = the user's turn was dispatched to the main agent. */
  onAgentState(s: MainState): void {
    if (s === this.main) return;
    this.main = s;
    if (s === 'first_audio' || s === 'speaking') this.live = null;
    if (s !== 'queued' && s !== 'thinking' && this.bridgeTimer) { clearTimeout(this.bridgeTimer); this.bridgeTimer = null; } // channel-1 responded: no bridge
    if (s === 'idle') {
      this.heard = ''; this.decidedAt = 0; this.bridged = false; this.cueFrom = 0;
      if (this.o.anticipate && this.reply.trim() && !this.plan && !this.planning) this.requestPlan(); // plan the user's reply to it
      return;
    }
    if (s === 'queued') {
      this.turnNo++; this.plan = null; this.planning = false; this.planTries = 0; this.revised = false; this.reply = '';
      // The user's turn is final: a pending pause/backchannel moment is stale (never a filler after they finished).
      this.gen++; this.userSpeaking = false; this.bridged = false;
      if (this.lastKind === 'bridge') this.lastKind = null; // one bridge per turn (bridged) — the last turn's bridge must not gate this one's
      for (const k of ['pauseTimer', 'bcTimer', 'bridgeTimer'] as const) { if (this[k]) clearTimeout(this[k]!); this[k] = null; }
      // A backchannel just before the end of their turn must not cost the bridge: wait out minGapMs too (one chance only —
      // live, a "right" at the last breath gated the 1.2s bridge and the slow answer started from silence).
      if (this.o.instantBridge) this.prefetchBridge();
      const wait = Math.max(this.o.bridgeDelayMs, this.o.minGapMs - (this.o.now() - this.lastAt));
      if (wait > 0) this.bridgeTimer = setTimeout(() => { this.bridgeTimer = null; void this.consider('working'); }, wait);
      else void this.consider('working');
    }
  }

  /** The user's final line was dispatched to the main agent (its words are the bridge's micro-context). */
  onUserTurn(text: string): void {
    this.heard = text.trim();
    this.floorWords = this.carried + words(this.heard).length;
    this.lastUser = this.heard;
    this.detect(this.heard);
    this.main = 'idle';
    this.onAgentState('queued');
  }

  /** The main answer's streamed text (before its audio): channel-1 is responding — no bridge from here on. */
  onMainTokens(text: string): void {
    this.noteReply(text);
    if (text.trim() && (this.main === 'queued' || this.main === 'thinking')) this.onAgentState('streaming_text');
  }

  /** Channel-1's reply text (a delta) — only the plan's context; no state change (hosts without onMainTokens). */
  noteReply(text: string): void { this.reply += text; }

  /** The user barged in: cut the in-flight line now and drop every pending decision. */
  onBargeIn(): void {
    this.gen++;
    this.cutLine = this.inflight || this.live ? this.live : null;
    if (this.inflight) { this.inflight = null; try { this.o.cancel(); } catch (e) { log.warn(`cancel threw: ${(e as Error).message}`); } }
  }

  /** Is `text` the line the last barge-in cut (ours, still holding the floor)? Hosts strip it from the cut reply — it is
   *  not the agent's speech. By identity, not by text: a real reply that merely repeats a recent line is kept. Consumed once. */
  spoke(text: string): boolean { const c = this.cutLine; this.cutLine = null; return !!c && c === text.trim(); }

  /** Resolves when the in-flight line finished playing (immediately when none) — hosts enqueue channel-1 audio behind it. */
  drain(): Promise<void> { return this.inflight ?? Promise.resolve(); }

  /** Channel-1's last reply ended on a CLOSED question (yes/no, "X or Y?", a confirm): the user's current turn answers or
   *  corrects it. An open invitation ("what happened?") is different — a story follows, and reactions fit it. */
  private get answeringUs(): boolean { return this.main === 'idle' && closedQuestion(this.reply); }
  private get audioLocked(): boolean { return this.main === 'first_audio' || this.main === 'speaking'; }
  private tail(): string { return words(this.heard).slice(-this.o.microWords).join(' '); }

  /** Hard gates every line passes, whoever proposed it. */
  private allowed(k: LineKind): boolean {
    if (!this.o.kinds.includes(k)) return false;
    if (this.audioLocked || this.inflight || this.o.now() - this.lastAt < this.o.minGapMs || (k === this.lastKind && (k !== 'backchannel' || this.o.now() - this.lastAt < this.o.backchannelGapMs))) return false; // backchannels may repeat (spaced by backchannelGapMs) across a long turn; lines never do (pick/line)
    if (k === 'backchannel') return this.o.aec && this.userSpeaking && this.main === 'idle' && !this.capped();
    if (k === 'filler') return !this.userSpeaking && this.main === 'idle';
    return (this.main === 'queued' || this.main === 'thinking') && !this.bridged; // bridge: only while channel-1 has said nothing
  }

  private bcTimes: number[] = [];
  private capped(): boolean {
    if (!this.o.maxPerWindow) return false;
    const from = this.o.now() - this.o.windowMs;
    this.bcTimes = this.bcTimes.filter((t) => t > from);
    return this.bcTimes.length >= this.o.maxPerWindow;
  }

  /** 0ms rule: the candidate line for this trigger, or silence. */
  private rule(trigger: Trigger, pauseMs = 0, breath = false, cue: Cue | null = null): ChannelKind {
    const w = words(this.heard);
    if (!this.lang) return 'silence'; // language not confidently known → never guess (no English to a Spanish speaker)
    if (w.length <= 3 || (w.length <= 6 && isPhatic(this.heard, this.bank))) return 'silence'; // greeting / quick turn bias, in the user's language
    const k: LineKind | null =
      trigger === 'interim' ? (this.backchannelMoment(w.length, breath) && (!this.o.anticipate || !this.plan || (!silentTurn(this.plan) && (!!cue || (!this.o.cueOnly && this.plan.density !== 'low')))) ? 'backchannel' : null) // a plan fires on its cues only
        : trigger === 'pause' ? (pauseMs >= this.o.fillerPauseMs && w.length >= this.o.fillerMinWords ? 'filler' : null)
          : w.length >= this.o.bridgeMinWords ? 'bridge' : null;
    return k && this.allowed(k) ? k : 'silence';
  }

  /** Plan (or, with `heard`, revise) the backchannels of the user's turn — async, never on the hot path. */
  private requestPlan(heard?: string): void {
    if (!this.o.plan || !this.lang) return;
    const turn = this.turnNo, t0 = this.o.now(), ac = new AbortController();
    if (!heard) { this.planning = true; this.planTries++; }
    const ctx: PlanContext = { agent: this.reply.trim(), user: words(this.lastUser).slice(-30).join(' '), recent: [...this.recent], lang: this.lang, heard };
    void withBudget(this.o.plan(ctx, ac.signal), this.o.planBudgetMs, heard ? 'plan revision' : 'plan').then((p) => {
      if (p === null) ac.abort();
      if (turn !== this.turnNo) return; // the turn it planned is over
      if (!heard) this.planning = false;
      if (!p) return;
      this.plan = p;
      this.o.onPlan(p, !!heard, Math.round(this.o.now() - t0));
    });
  }

  /** A clause boundary mid-floor: a breath or a comma-like close. Not a question (it wants the answer, not "mm-hm"), and a
   *  sentence end only deep into a story — a short request's end is the end of their turn (replay: "mm-hm" right after
   *  "Check hacker news for recent news.", just before the final). */
  private backchannelMoment(n: number, breath: boolean): boolean {
    if (/\?$/.test(this.heard) || pendingQuestion(this.heard)) return false;
    if (this.carried) n += this.carried; // a statement going on past an endpoint: its earlier words count; not a fresh request
    else if (n < this.o.requestHoldWords && requestOpener(this.heard)) return false;
    if (n < this.o.backchannelMinWords) return false;
    if (/[.!]$/.test(this.heard)) return n >= 2 * this.o.backchannelMinWords;
    return breath || /[,;:…]$/.test(this.heard);
  }

  private async consider(trigger: Trigger, pauseMs?: number, breath = false, cue: Cue | null = null): Promise<void> {
    if (this.busy) return;
    const n0 = words(this.heard).length;
    this.decidedAt = n0;
    if (!(this.o.anticipate && this.plan && trigger === 'interim')) cue = null;
    const opts = this.options as SecondChannelOptions; // aec may be a live getter (the host's engine tier): read at the decision
    if (opts.mode === 'chatty' && !opts.aec && !this.degradedLogged) { this.degradedLogged = true; log.info('chatty mode without echo cancellation — normal timing (our lines would re-trigger the mic)'); }
    const rule = this.rule(trigger, pauseMs, breath, cue);
    if (rule === 'silence') return;
    this.busy = true;
    const gen = this.gen, t0 = this.o.now();
    const ev = (e: Partial<ChannelEvent> & Pick<ChannelEvent, 'acted'>) => this.o.onEvent({ trigger, rule, ms: Math.round(this.o.now() - t0), ...(cue ? { cue: cue.line } : {}), ...e });
    const stale = () => gen !== this.gen || words(this.heard).length - n0 >= this.o.staleWords;
    try {
      let kind: ChannelKind = rule;
      let choice: string | undefined;
      // instantBridge: no refiner on a bridge — the rule already knows MAIN is silent; a yes/no there only cost first sound.
      if (this.o.decide && !cue && !(rule === 'bridge' && this.o.instantBridge)) { // a planned cue was decided ahead — no refiner on the hot path
        const ac = new AbortController();
        const d = await withBudget(this.o.decide(this.tail(), { ...this.state(pauseMs), rule }, ac.signal), this.o.decisionBudgetMs, 'channel decision', ac);
        choice = d?.choice;
        // in budget: the refiner wins (incl. a veto) — but a kind this moment can't take is not a veto, it's a misread
        // (live Para phone call: "backchannel" for the working moment gated the bridge → 4s of dead air): the rule stands
        if (choice && choice in CHANNEL_ACTIONS && (choice === 'silence' || this.allowed(choice as LineKind))) kind = choice as ChannelKind;
        else if (choice && choice in CHANNEL_ACTIONS) log.debug(`decider chose ${choice} for a ${rule} moment — the rule stands`);
      }
      if (kind === 'silence') return ev({ choice, acted: 'silence' });
      if (stale()) return ev({ choice, kind, acted: 'stale' });
      if (!this.allowed(kind)) return ev({ choice, kind, acted: 'gated' });
      // heavy lines spaced; light in between. The user ANSWERING our question (or correcting what it assumed) gets a light
      // cue at most — a reaction ("wait, really?") there reads as surprise at their own answer (live 20261014-105404).
      const react = kind === 'backchannel' && !this.answeringUs && this.o.now() - this.lastReactAt >= this.o.reactionGapMs;
      const text = cue && react && !this.recent.some((r) => norm(r) === norm(cue!.line)) ? cue.line : await this.line(kind, react);
      if (!text) return ev({ choice, kind, acted: 'no_bank' }); // no bank in the user's language yet → silence, never English
      if (stale()) return ev({ choice, kind, text, acted: 'stale' });
      if (!this.allowed(kind)) return ev({ choice, kind, text, acted: 'gated' }); // channel-1 audio started meanwhile
      const said = this.say(kind, text);
      if (said && cue) this.cueFrom = words(this.heard).length;
      if (said && kind === 'backchannel' && this.o.reactionGapMs && !this.bank?.backchannel.some((b) => norm(b) === norm(text))) this.lastReactAt = this.o.now();
      ev({ choice, kind, text, acted: said ? 'spoken' : 'skipped' });
    } finally {
      this.busy = false;
    }
  }

  private state(pauseMs?: number): Record<string, unknown> {
    const now = this.o.now();
    return {
      user_speaking: this.userSpeaking,
      pause_ms: pauseMs ?? null,
      agent_state: this.main,
      last_line: this.recent.at(-1) ?? null,
      ms_since_last_line: Number.isFinite(this.lastAt) ? Math.round(now - this.lastAt) : null,
      call_ms: Math.round(now - this.startedAt),
    };
  }

  /** Start writing this turn's bridge now, so it may be ready by bridgeDelayMs. The bridge never waits for it. */
  private prefetchBridge(): void {
    this.bridgeDraft = null;
    if (!this.o.generate || !this.o.generateFor.includes('bridge') || !this.lang || !this.heard) return;
    const gen = this.gen, ac = new AbortController();
    const ctx: ChannelContext = { kind: 'bridge', heard: this.tail(), state: this.main, avoid: [...this.recent], lang: this.lang };
    void withBudget(this.o.generate(ctx, ac.signal), Math.max(this.o.generateBudgetMs, this.o.bridgeDelayMs), 'bridge generate', ac)
      .then((raw) => { const text = cleanLine(raw ?? '', ctx.lang); if (text && gen === this.gen) this.bridgeDraft = { gen, text }; })
      .catch((e) => log.debug(`bridge prefetch failed: ${(e as Error)?.message ?? e}`));
  }

  private async line(kind: LineKind, react = true): Promise<string> {
    if (kind === 'bridge' && this.o.instantBridge) { // canned at once; the generated one only if it already landed (never wait for it)
      const d = this.bridgeDraft?.gen === this.gen ? this.bridgeDraft.text : '';
      this.bridgeDraft = null;
      if (d && !this.recent.some((r) => norm(r) === norm(d))) return d;
      return this.pick(kind);
    }
    if (this.o.generate && this.o.generateFor.includes(kind) && (kind !== 'backchannel' || react)) {
      const ac = new AbortController();
      const ctx: ChannelContext = { kind, heard: this.tail(), state: this.main, avoid: [...this.recent], lang: this.lang!, ...(kind === 'backchannel' && this.o.reactionGapMs ? { react: true } : {}) };
      const raw = await withBudget(this.o.generate(ctx, ac.signal), this.o.generateBudgetMs, `${kind} generate`);
      if (raw === null) ac.abort();
      const text = cleanLine(raw ?? '', this.lang!);
      if (text && !this.recent.some((r) => norm(r) === norm(text))) return text;
      if (text) log.debug(`generated "${text}" repeats a recent line — canned`);
    }
    return this.pick(kind);
  }

  /** Canned rotation in the user's language: never one of the last `bankExclude` picks of this kind, nor a recent
   *  line. '' = no bank for this language yet (silence). */
  private pick(kind: LineKind): string {
    const pool = this.bank?.[kind] ?? [];
    if (!pool.length) return '';
    const hist = this.picks.get(kind) ?? [];
    const recent = new Set([...hist.slice(-this.o.bankExclude), ...this.recent].map(norm));
    const fresh = pool.filter((x) => !recent.has(norm(x)));
    const from = fresh.length ? fresh : pool;
    const p = from[Math.floor(this.o.random() * from.length)];
    this.picks.set(kind, [...hist, p].slice(-this.o.bankExclude));
    return p;
  }

  private say(kind: LineKind, text: string): boolean {
    let r: ReturnType<SecondChannelOptions['speak']>;
    try { r = this.o.speak(text, kind); } catch (e) { log.warn(`speak threw — line dropped: ${(e as Error).message}`); return false; }
    if (r === false) return false;
    this.lastAt = this.o.now();
    this.lastKind = kind;
    if (kind === 'backchannel') this.bcTimes.push(this.lastAt);
    if (kind === 'bridge') this.bridged = true;
    this.recent = [...this.recent, text].slice(-this.o.avoidLines);
    this.live = text.trim();
    this.o.show(text, kind);
    const p: Promise<void> = Promise.resolve(r)
      .then(() => {}, (e) => { log.warn(`channel line playback failed: ${(e as Error)?.message ?? e}`); })
      .finally(() => { if (this.inflight === p) this.inflight = null; });
    this.inflight = p;
    return true;
  }
}

/** Per-moment yes/no question for the fast refiner — what an attentive human listener would do at THIS kind of moment. */
const FAST_ASK: Record<LineKind, string> = {
  backchannel: 'The user is still talking and just took a breath. Would an attentive human listener say a tiny "mm-hm" now? ' +
    'Yes ONLY if they are telling a story/explaining across several clauses. No if it is a request, command, question, or they are mid-request.',
  filler: 'The user went quiet mid-turn. Would a human listener make a soft "hmm…" now? Yes ONLY if their sentence is unfinished and ' +
    'they are searching for words. No if they said a complete request, question, answer, thanks or remark (the reply comes next).',
  bridge: 'The user finished and the assistant\'s answer is taking a moment. Would a human say a short "let me check—" now? Yes if they ' +
    'asked for real work or a substantial/hard question. No for small talk, a greeting, thanks, an ok/yes/agreement.',
};

/** The channel refiner (yes/no on a tiny prompt, one output token): the generic Choice decider measured p50 ~800ms /
 *  p95 ~1250ms on groq gpt-oss-120b against the 300ms budget — every call dropped, the rule alone spoke. groq qwen3.8-27b
 *  (reasoning off) measured p50 105ms / p95 154ms (backchannel: 74/80 on labelled partials, en+he). Answers the rule's own
 *  kind: yes → that kind, no → silence (a veto), anything else → null (the rule stands). */
export function fastChannelDecider(ai: ChatLike, model = 'groq/qwen/qwen3.8-27b'): NonNullable<SecondChannelOptions['decide']> {
  return async (heard, state, signal) => {
    const kind = state.rule as LineKind;
    if (!(kind in FAST_ASK)) return null;
    const prompt = `Live voice call. ${FAST_ASK[kind]}\nSo far the user said: "${heard}"\nAnswer yes or no.`;
    const reasoning = /qwen/.test(model) ? { providerOptions: { reasoning_effort: 'none' } } : /gpt-oss/.test(model) ? { providerOptions: { reasoning_effort: 'low' } } : {};
    const r = (await ai.chat({ model, messages: [{ role: 'user', content: prompt }], stream: false, signal, temperature: 0, maxTokens: /gpt-oss/.test(model) ? 200 : 3, ...reasoning })) as ChatResponse;
    const a = (r.content ?? '').trim().toLowerCase();
    return /^yes/.test(a) ? { choice: kind } : /^no/.test(a) ? { choice: 'silence' } : null;
  };
}

/** The channel refiner hosts wire: every moment → fastChannelDecider (AGENTX_CHANNEL_DECIDER_MODEL, else groq qwen; needs
 *  GROQ_API_KEY); without a fast model, the instinct's Choice (slow — mostly over budget). */
export function channelDecide(ai: ChatLike, instinct?: Pick<Instinct, 'channel'>, env: Record<string, string | undefined> = process.env): SecondChannelOptions['decide'] {
  const model = env.AGENTX_CHANNEL_DECIDER_MODEL || undefined;
  if (model || env.GROQ_API_KEY) return fastChannelDecider(ai, model);
  return instinct ? (heard, state) => instinct.channel(heard, state) : undefined;
}

const KIND_HINT: Record<LineKind, string> = {
  backchannel: 'a tiny listener cue while they keep talking ("mm-hm", "right", "yeah") — shows you follow, never takes the turn',
  filler: 'a soft listener sound while THEY search for words ("mm…", "mhm", "yeah…") — never a word that continues their sentence or starts yours',
  bridge: 'a neutral one-moment cue while the answer is prepared ("let me see…", "one sec…") — never the start of a statement or opinion ("the thing is", "well actually")',
};

const REACT_HINT = 'a short reaction to their LAST words instead: feeling ("oh no", "ha, nice", "ugh"), surprise ("wait, really?", "huh"), ' +
  'a light laugh if they joke or laugh ("ha!"), or an echo of their key word ("the deploy?") — still never asks for an answer or takes the turn; ' +
  'if nothing there deserves a reaction, a plain "mm-hm"';

/** A fast-model generator with a micro-context prompt (no history, no system bloat). */
export function fastLineGenerator(ai: ChatLike, model: string, gender?: Gender): NonNullable<SecondChannelOptions['generate']> {
  return async (c, signal) => {
    const prompt = `You voice the tiny side-sounds of a live spoken conversation. Write ONE line of 1-5 words: ${KIND_HINT[c.kind]}.\n` +
      (c.react ? `Here the line may be ${REACT_HINT}.\n` : '') +
      `The user just said: "${c.heard}"\nThe assistant is: ${c.state}.\n` +
      (c.avoid.length ? `Do not reuse any of: ${c.avoid.map((a) => `"${a}"`).join(', ')}.\n` : '') +
      `React to their actual words, casually, in ${languageName(c.lang)}. ${personaHint(gender)} Output only the line.`;
    // gpt-oss (the default reflex) is a reasoning model: at a tiny maxTokens its REASONING came back as the line
    // ("The user wants: …"); low effort + room for it measured ~200ms p50 on groq with clean lines.
    const reasoning = /gpt-oss/.test(model) ? { providerOptions: { reasoning_effort: 'low' } } : {};
    const r = (await ai.chat({ model, messages: [{ role: 'user', content: prompt }], stream: false, signal, maxTokens: 150, ...reasoning })) as ChatResponse;
    return r.content ?? '';
  };
}

/** The reply's last sentence is a closed question: it opens on an auxiliary ("should I…?", "do you want…?") or offers a
 *  choice ("…X or Y?"). Lead-ins ("Just to confirm,", "So,") and inline tags are skipped. */
export function closedQuestion(reply: string): boolean {
  const t = reply.replace(/\[[^\]]*\]|<\/?[a-z]+>/gi, ' ').replace(/\s+/g, ' ').trim();
  if (!/\?["'’”)]*$/.test(t)) return false;
  const q = t.split(/(?<=[.!?])\s+(?=\S)/).pop()!.replace(/^(?:(?:so|ok(?:ay)?|and|but|just to (?:confirm|check)|to confirm|quick (?:check|question)|got it)[,:—–-]*\s+)+/i, '');
  return /^(?:should|shall|do|does|did|is|are|was|were|can|could|would|will|may|have|has|want|wanna)\b/i.test(q) || /\bor\b/i.test(q);
}
